import { useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { AutoTagsViewAllDialog } from "../RegulationDetails/SummarySection/AutoTagsViewAllDialog";
import {
  autoTagEmptyDisplay,
  type RegulationAutoTagGroup,
} from "../../services/regulationAutoTags";

const cellRootSx = {
  display: "flex",
  alignItems: "center",
  height: "100%",
  width: "100%",
  minWidth: 0,
  overflow: "hidden",
};

type AutoTagsListCellProps = Readonly<{
  value?: string;
  groups?: RegulationAutoTagGroup[];
  /** True when API included `auto_tags` (even if empty). */
  fieldPresent?: boolean;
}>;

/**
 * List Auto Tags cell:
 * - `auto_tags` absent → "NA"
 * - `auto_tags` present but empty → "-"
 * - otherwise clickable text that opens the View All dialog
 */
export function AutoTagsListCell({
  value,
  groups = [],
  fieldPresent = false,
}: AutoTagsListCellProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const text = typeof value === "string" ? value.trim() : "";
  const hasValues =
    text.length > 0 &&
    groups.some(
      (group) => Array.isArray(group.values) && group.values.length > 0,
    );

  if (!hasValues) {
    const placeholder = autoTagEmptyDisplay(fieldPresent);
    return (
      <Box sx={cellRootSx}>
        <Typography
          variant="body2"
          noWrap={placeholder !== "-"}
          color={placeholder === "-" ? "text.secondary" : "inherit"}
          sx={{
            width: "100%",
            maxWidth: "100%",
            lineHeight: 1.4,
            overflow: "hidden",
            textOverflow: placeholder !== "-" ? "ellipsis" : undefined,
          }}
        >
          {placeholder}
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box sx={cellRootSx}>
        <Typography
          variant="body2"
          noWrap
          title="Click to view details"
          component="button"
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            setDialogOpen(true);
          }}
          sx={{
            width: "100%",
            maxWidth: "100%",
            lineHeight: 1.4,
            textAlign: "left",
            overflow: "hidden",
            textOverflow: "ellipsis",
            cursor: "pointer",
            color: "inherit",
            border: "none",
            background: "none",
            p: 0,
            font: "inherit",
          }}
        >
          {text}
        </Typography>
      </Box>
      <AutoTagsViewAllDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        groups={groups}
      />
    </>
  );
}
